<template>
  <div class="c--shared_currencies-list" :class="`currencies-list-wrapper type-modal searchable}`">
    <div class="currencies-list-container" data-testid="currencies-list">
      <box-simple :shadowed="isShadowedBox" :rounded="isRoundedBox" class="w-100">
        <div class="currencies-list-header px-20">
          <p class="title-sub">{{ title }}</p>
        </div>
        <div class="currencies-list-search-container">
          <span class="icon search">
            <i class="airalo-icon-search" />
          </span>
          <input
            v-model="searchValue"
            :placeholder="searchInputPlaceholder"
            class="currencies-list-search w-100"
            data-testid="currencies-list-search-bar"
            type="text"
          />
          <span class="icon close-icon" v-if="searchValue.length > 0" @click.prevent="clearSearch">
            <i class="airalo-icon-close-round-filled" />
          </span>
        </div>
        <ul
          class="currencies-list"
          :class="{
            'empty-list': !searchedCurrencyList.length,
            'd-grid': searchedCurrencyList.length > 0,
          }"
        >
          <li
            v-for="(currency, index) in searchedCurrencyList"
            :key="`popup-currencies-list-${index}`"
            :class="{
              active: currency.code === $i18n.locale,
              'is-browser-locale': browserLocale === currency.code,
            }"
          >
            <div class="currency-name">
              <a
                @click.prevent="onCurrencySelected(currency)"
                class="d-flex justify-content-between align-items-center"
                :class="{ 'tw-font-semibold': currency.code === currentCurrencyCode }"
                :data-testid="`${currency.code}-currency-select`"
                :dir="$dir()"
              >
                <span :class="{ 'text-right': $dir() === 'rtl' }">{{ currencyFullName(currency) }}</span>
                <i v-if="currency.code === currentCurrencyCode" class="airalo-icon-tick-round-filled" />
              </a>
            </div>
          </li>
          <li v-if="!searchedCurrencyList.length" class="empty">
            <p>{{ emptyText || $t('global.no-currency-available') }}</p>
          </li>
        </ul>
      </box-simple>
    </div>
    <client-only>
      <confirm-currency-change-modal
        :modal-view="isConfirmCurrencyChangeModalShown"
        :overlay-view="false"
        type="double"
        :text="currencyChangeModalTitle"
        :button="{
          text: $t('global.cancel'),
          isClose: true,
          // class: 'button button-secondary-outline',
        }"
        :button-second="{
          text: $t('components.change.currency.positive-button'),
          class: 'btn-prompt-simple-secondary',
        }"
        @onCloseModal="onCancelCurrencyChangeModal"
        @onClickButton="onConfirmCurrencyChangeModal"
      >
        <p class="tw-text-14 tw-mt-4 text-center">
          {{ $t('components.confirm-currency-change.info') }}
        </p>
      </confirm-currency-change-modal>
    </client-only>
  </div>
</template>

<script>
  import ClientSideCookies from 'js-cookie'

  export default {
    components: {
      BoxSimple: () => import('~/components/card-boxes/box-type-simple.vue'),
      ConfirmCurrencyChangeModal: () => import('~/components/modals/prompt-simple.vue'),
      CountryNameInFlag: () => import('~/components/shared/country-name-in-flag.vue'),
    },

    props: {
      list: {
        type: Array,
        required: true,
      },
      title: {
        type: String,
        required: true,
      },
      description: {
        type: String,
        required: false,
        default: '',
      },
      emptyText: {
        type: String,
        required: false,
        default: '',
      },
      searchInputPlaceholder: {
        type: String,
        required: false,
        default: '',
      },
      isShadowedBox: {
        type: Boolean,
        required: false,
        default: false,
      },
      isRoundedBox: {
        type: Boolean,
        required: false,
        default: false,
      },
    },

    data() {
      return {
        isConfirmCurrencyChangeModalShown: false,
        newCurrency: null,
        searchValue: '',
      }
    },

    computed: {
      browserLocale() {
        return this.$i18n.getBrowserLocale()
      },

      currencyChangeModalTitle() {
        return this.$t('components.confirm-currency-change.title', {
          currencyFromSymbol: this.currentCurrencySymbol,
          currencyFromCode: this.currentCurrencyCode,
          currencyToSymbol: this.newCurrency?.symbol,
          currencyToCode: this.newCurrency?.code,
        })
      },

      currentCurrencyCode() {
        return this.$store.getters['multiCurrency/get_current_currency_code']
      },

      currentCurrencySymbol() {
        return this.$store.getters['multiCurrency/get_current_currency_symbol']
      },

      searchedCurrencyList() {
        if (this.searchValue) {
          return this.list.filter((currency) => {
            return [currency.title, currency.name, currency.code, currency.symbol]
              .join(',')
              .toLowerCase()
              .includes(this.searchValue.toLowerCase())
          })
        } else {
          return this.list
        }
      },
    },

    methods: {
      async changeCurrency(currency) {
        if (currency.code !== this.currentCurrencyCode) {
          const prevCurrency = this.currentCurrencyCode
          await this.$store.dispatch('multiCurrency/set_currency', {
            newCurrencyCode: currency.code,
            shouldUpdateProfile: true,
          })
          this.$utils.sendEvent('currencyChange', {
            previous_currency: prevCurrency,
            current_currency: currency.code,
          })
          window.location.reload()
        }
      },

      clearSearch() {
        this.searchValue = ''
      },

      currencyFullName(currency) {
        return currency.title
      },

      onCancelCurrencyChangeModal() {
        this.isConfirmCurrencyChangeModalShown = false
      },

      onConfirmCurrencyChangeModal() {
        this.changeCurrency(this.newCurrency)
        this.$nextTick(() => {
          this.isConfirmCurrencyChangeModalShown = false
          this.$emit('closeCurrencyModal')
        })
      },

      onCurrencySelected(currency) {
        this.newCurrency = currency
        this.$nextTick(() => {
          this.isConfirmCurrencyChangeModalShown = true
        })
      },
    },
  }
</script>

<style lang="scss" scoped>
  .currencies-list {
    overflow: auto;

    li {
      padding: 0;
      border-bottom: 1px solid #eeeeee;
    }

    &-container {
      display: flex;
      align-items: stretch;
    }

    &-search {
      border: none;
      border-radius: 0;
      background-color: transparent;
      padding: 0;
      min-height: 22px;

      color: #4a4a4a;
      font-size: 0.9375rem;
      letter-spacing: 0;
      line-height: 1;
      font-weight: 400;

      &::placeholder,
      &:-ms-input-placeholder,
      &::-ms-input-placeholder {
        color: #8a8a8a;
      }

      &-container {
        position: relative;
        display: flex;
        justify-content: space-between;
        align-items: center;

        border: 1px solid #dddddd;
        border-radius: 7px;
        background-color: #fafafa;
        padding: 13px 0;

        margin: 20px;

        & + .currencies-list {
          border-top: 1px solid #eeeeee;
        }

        .icon {
          position: relative;
          display: flex;

          &.search {
            margin-left: 20px;
            margin-right: 13px;

            [dir='rtl'] & {
              margin-right: 20px;
              margin-left: 13px;
            }
          }

          &.close-icon {
            margin-right: 17px;
            margin-left: 13px;
            cursor: pointer;

            [dir='rtl'] & {
              margin-right: 13px;
              margin-left: 17px;
            }
          }
        }
      }
    }

    &-wrapper {
      &.type-modal {
        .currencies-list {
          padding: 0 0 0 20px;

          [dir='rtl'] & {
            padding: 0 20px 0 0;
          }

          &-container {
            height: 100%;
          }

          &.empty-list {
            display: flex;
            justify-content: center;
            align-items: center;
            flex: 1;
          }

          li {
            padding: 0 20px 0 0;

            [dir='rtl'] & {
              padding: 0 0 0 20px;
            }

            &.empty {
              border-bottom: none;

              p {
                color: #8a8a8a;
                margin: 0;
                font-size: 0.9375rem;
                font-weight: 400;
                line-height: 1.4;
                letter-spacing: 0;
              }
            }

            &.is-browser-locale {
              grid-row-start: 1;
            }

            .currency-name {
              font-weight: 400;
              width: 100%;

              a {
                color: #4a4a4a;
                cursor: pointer;
                font-size: 0.9375rem;
                height: 58px;
                letter-spacing: 0;
                line-height: 1.3333;
                min-height: 58px;
                text-decoration: none;
              }
            }
          }
        }
      }
    }
  }
</style>
